import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import { Button } from '@/components/ui/button';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PlusCircle, Save, Trash2, Loader } from 'lucide-react';
import { toast } from 'sonner';
import { HotelData } from '@/lib/types/auth';
import ChatWidget from '@/components/chat/ChatWidget';
import { getHotelData, updateHotelData } from '@/lib/services/hotelService';
import { useAuth } from '@/lib/context/AuthContext';
import FactSheetUpload from '@/components/dashboard/FactSheetUpload';
import EmbedCode from '@/components/dashboard/EmbedCode';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [isLoading, setIsLoading] = useState(true);
  const { auth } = useAuth();
  
  const [isSaving, setIsSaving] = useState(false);
  
  const [hotelInfo, setHotelInfo] = useState<HotelData>({
    name: '',
    check_in_time: '15:00',
    check_out_time: '11:00',
    services: [],
    faqs: [],
    contact_email: '',
    contact_phone: '',
    address: '',
    website: '',
    fact_sheet_text: ''
  });
  
  const [newService, setNewService] = useState('');
  const [newFaq, setNewFaq] = useState<{question: string; answer: string}>({
    question: '',
    answer: ''
  });

  useEffect(() => {
    const loadHotelData = async () => {
      if (auth.user) {
        setIsLoading(true);
        try {
          const data = await getHotelData();
          if (data) {
            setHotelInfo(data);
          }
        } catch (error) {
          console.error('Error loading hotel data:', error);
          toast.error('Failed to load hotel data');
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadHotelData();
  }, [auth.user]);
  
  const handleAddService = () => {
    if (newService.trim()) {
      setHotelInfo({
        ...hotelInfo,
        services: [...hotelInfo.services, newService.trim()]
      });
      setNewService('');
      toast.success('Service added successfully');
    }
  };
  
  const handleRemoveService = (index: number) => {
    const updatedServices = [...hotelInfo.services];
    updatedServices.splice(index, 1);
    setHotelInfo({
      ...hotelInfo,
      services: updatedServices
    });
    toast.success('Service removed');
  };
  
  const handleAddFaq = () => {
    if (newFaq.question.trim() && newFaq.answer.trim()) {
      const newFaqWithId = {
        id: Date.now().toString(),
        question: newFaq.question.trim(),
        answer: newFaq.answer.trim()
      };
      
      setHotelInfo({
        ...hotelInfo,
        faqs: [...hotelInfo.faqs, newFaqWithId]
      });
      
      setNewFaq({
        question: '',
        answer: ''
      });
      
      toast.success('FAQ added successfully');
    } else {
      toast.error('Please provide both question and answer');
    }
  };
  
  const handleRemoveFaq = (id: string) => {
    setHotelInfo({
      ...hotelInfo,
      faqs: hotelInfo.faqs.filter(faq => faq.id !== id)
    });
    toast.success('FAQ removed');
  };
  
  const handleSaveFactSheet = async (text: string) => {
    setIsSaving(true);
    try {
      const updatedHotelInfo = {
        ...hotelInfo,
        fact_sheet_text: text
      };
      
      const { success, error } = await updateHotelData(updatedHotelInfo);
      
      if (success) {
        setHotelInfo(updatedHotelInfo);
        toast.success('Fact sheet saved successfully');
      } else {
        toast.error(`Failed to save: ${error?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving fact sheet:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { success, error } = await updateHotelData(hotelInfo);
      if (success) {
        toast.success('Hotel information saved successfully');
      } else {
        toast.error(`Failed to save: ${error?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving hotel data:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsSaving(false);
    }
  };
  
  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <Loader className="mx-auto h-8 w-8 animate-spin text-hotel-primary" />
            <p className="mt-2 text-gray-600">Loading your hotel information...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="grid grid-cols-1 gap-6">
        <div className="lg:col-span-2">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-6 bg-white border">
              <TabsTrigger value="general">General Info</TabsTrigger>
              <TabsTrigger value="services">Services</TabsTrigger>
              <TabsTrigger value="faqs">FAQs</TabsTrigger>
              <TabsTrigger value="factsheet">Fact Sheet</TabsTrigger>
            </TabsList>
            
            <TabsContent value="general">
              <Card className='border-none'>
                <CardHeader className='p-2 mb-6'>
                  <CardTitle>Hotel Information</CardTitle>
                  <CardDescription>
                    Basic information about your hotel used to power the AI assistant
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 p-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="hotelName">Hotel Name</Label>
                      <Input 
                        id="hotelName" 
                        value={hotelInfo.name}
                        onChange={(e) => setHotelInfo({...hotelInfo, name: e.target.value})}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="website">Booking URL</Label>
                      <Input 
                        id="website" 
                        value={hotelInfo.website}
                        onChange={(e) => setHotelInfo({...hotelInfo, website: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="checkInTime">Check-in Time</Label>
                      <Input 
                        id="checkInTime" 
                        value={hotelInfo.check_in_time}
                        onChange={(e) => setHotelInfo({...hotelInfo, check_in_time: e.target.value})}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="checkOutTime">Check-out Time</Label>
                      <Input 
                        id="checkOutTime" 
                        value={hotelInfo.check_out_time}
                        onChange={(e) => setHotelInfo({...hotelInfo, check_out_time: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contactEmail">Contact Email</Label>
                      <Input 
                        id="contactEmail" 
                        type="email"
                        value={hotelInfo.contact_email}
                        onChange={(e) => setHotelInfo({...hotelInfo, contact_email: e.target.value})}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="contactPhone">Contact Phone</Label>
                      <Input 
                        id="contactPhone" 
                        value={hotelInfo.contact_phone}
                        onChange={(e) => setHotelInfo({...hotelInfo, contact_phone: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <Input 
                      id="address" 
                      value={hotelInfo.address}
                      onChange={(e) => setHotelInfo({...hotelInfo, address: e.target.value})}
                    />
                  </div>
                  
                  <Button 
                    onClick={handleSave}
                    className="bg-hotel-primary hover:bg-hotel-primary/90"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Loader className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="services">
              <Card>
                <CardHeader>
                  <CardTitle>Hotel Services</CardTitle>
                  <CardDescription>
                    Add the services and amenities your hotel offers
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex space-x-2">
                      <Input 
                        placeholder="Add a new service (e.g., Free WiFi)"
                        value={newService}
                        onChange={(e) => setNewService(e.target.value)}
                      />
                      <Button 
                        onClick={handleAddService}
                        className="bg-hotel-light text-dark hover:bg-hotel-light/50"
                      >
                        <PlusCircle className="h-4 w-4 mr-2" />
                        Add
                      </Button>
                    </div>
                    <div className='grid grid-cols-1 lg:grid-cols-6'>
                    {hotelInfo.services.length > 0 ? (
                      <div className="border rounded-md divide-y">
                        {hotelInfo.services.map((service, index) => (
                          <div 
                            key={index} 
                            className="flex items-center justify-between p-3 hover:bg-gray-50"
                          >
                            <span>{service}</span>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleRemoveService(index)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center p-6 border rounded-md bg-gray-50">
                        <p className="text-gray-500">No services added yet</p>
                      </div>
                    )}
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleSave}
                    className="bg-hotel-primary hover:bg-hotel-primary/90"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Loader className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="faqs">
              <Card>
                <CardHeader>
                  <CardTitle>Frequently Asked Questions</CardTitle>
                  <CardDescription>
                    Add common questions and answers about your hotel
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="space-y-3 border rounded-md p-4">
                      <div className="space-y-2">
                        <Label htmlFor="question">Question</Label>
                        <Input 
                          id="question"
                          placeholder="E.g., What time is breakfast served?"
                          value={newFaq.question}
                          onChange={(e) => setNewFaq({...newFaq, question: e.target.value})}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="answer">Answer</Label>
                        <Textarea 
                          id="answer"
                          placeholder="E.g., Breakfast is served from 6:30 AM to 10:30 AM in our main restaurant."
                          value={newFaq.answer}
                          onChange={(e) => setNewFaq({...newFaq, answer: e.target.value})}
                          rows={3}
                        />
                      </div>
                      
                      <Button 
                        onClick={handleAddFaq}
                        className="bg-hotel-light text-dark hover:bg-hotel-light/50"
                      >
                        <PlusCircle className="h-4 w-4 mr-2" />
                        Add FAQ
                      </Button>
                    </div>
                    
                    {hotelInfo.faqs.length > 0 ? (
                      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                        {hotelInfo.faqs.map((faq) => (
                          <div 
                            key={faq.id} 
                            className="border rounded-md p-4 hover:bg-gray-50"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="font-medium">{faq.question}</h3>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => handleRemoveFaq(faq.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 -mt-1 -mr-1"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                            <p className="text-gray-600">{faq.answer}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center p-6 border rounded-md bg-gray-50">
                        <p className="text-gray-500">No FAQs added yet</p>
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    onClick={handleSave}
                    className="bg-hotel-primary hover:bg-hotel-primary/90"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Loader className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="factsheet">
              <FactSheetUpload 
                factSheetText={hotelInfo.fact_sheet_text}
                onSaveFactSheet={handleSaveFactSheet}
                isSaving={isSaving}
              />
            </TabsContent>
          </Tabs>
        </div>

        <div className="lg:col-span-1 space-y-6 hidden">
          <Card>
            <CardHeader>
              <CardTitle>Chat Preview</CardTitle>
              <CardDescription>
                This is how your chat widget will appear on your website
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center pt-4">
              <div className="w-full max-w-sm">
                <ChatWidget 
                  hotelInfo={{
                    id: hotelInfo.id,
                    name: hotelInfo.name,
                    checkInTime: hotelInfo.check_in_time,
                    checkOutTime: hotelInfo.check_out_time,
                    services: hotelInfo.services,
                    faqs: hotelInfo.faqs.map(faq => ({
                      id: faq.id,
                      question: faq.question,
                      answer: faq.answer
                    })),
                    contactEmail: hotelInfo.contact_email,
                    contactPhone: hotelInfo.contact_phone,
                    address: hotelInfo.address,
                    website: hotelInfo.website
                  }}
                  factSheetText={hotelInfo.fact_sheet_text}
                  isMockup={false}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
